import {
  fetchCertificationModules,
  fetchMyCertifications,
  fetchTechnicians,
  fetchProgressClaims,
  type Certification,
  type CertificationModule,
  type Technician,
  type ProgressClaim,
} from "./certifications";
import { fetchRewardRules, type RewardRule } from "./certification-reward-rules";

export const certificationQueryKeys = {
  modules: (opts?: { includeInactive?: boolean }) =>
    ["certification-modules", { includeInactive: Boolean(opts?.includeInactive) }] as const,
  myCertifications: () => ["certification-me"] as const,
  technicians: () => ["certification-technicians"] as const,
  progressClaims: (opts?: { status?: string; search?: string }) =>
    ["certification-progress-claims", opts?.status ?? "all", opts?.search ?? ""] as const,
  rewardRules: () => ["reward-rules"] as const,
};

export function certificationModulesQueryOptions(
  accessToken: string,
  opts?: { includeInactive?: boolean },
) {
  return {
    queryKey: certificationQueryKeys.modules(opts),
    queryFn: (): Promise<CertificationModule[]> =>
      fetchCertificationModules(accessToken, opts),
    staleTime: 60_000,
  };
}

export function myCertificationsQueryOptions(accessToken: string) {
  return {
    queryKey: certificationQueryKeys.myCertifications(),
    queryFn: (): Promise<Certification[]> => fetchMyCertifications(accessToken),
    staleTime: 30_000,
  };
}

export function certificationTechniciansQueryOptions(accessToken: string) {
  return {
    queryKey: certificationQueryKeys.technicians(),
    queryFn: async (): Promise<Array<Technician & { id: string }>> => {
      const rows = await fetchTechnicians(accessToken);
      return Array.isArray(rows)
        ? rows.map((tech) => ({ ...tech, id: tech.userId }))
        : [];
    },
    staleTime: 60_000,
  };
}

export function certificationProgressClaimsQueryOptions(
  accessToken: string,
  opts?: { status?: "all" | "pending" | "claimed"; search?: string },
) {
  return {
    queryKey: certificationQueryKeys.progressClaims(opts),
    queryFn: (): Promise<ProgressClaim[]> => fetchProgressClaims(accessToken, opts),
    staleTime: 30_000,
  };
}

export function rewardRulesQueryOptions(accessToken: string) {
  return {
    queryKey: certificationQueryKeys.rewardRules(),
    queryFn: (): Promise<RewardRule[]> => fetchRewardRules(accessToken),
    staleTime: 60_000,
  };
}
